import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { getHours, updateHours } from '../../services/restaurantService';

const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

function HoursEditor({ restaurantId, onChange }) {
  const [hoursData, setHoursData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Initialize hours data structure
  useEffect(() => {
    setHoursData(
      DAYS_OF_WEEK.reduce((acc, day) => {
        acc[day] = {
          isOpen: true,
          openTime: '09:00',
          closeTime: '21:00'
        };
        return acc;
      }, {})
    );
  }, []);

  // Load hours if restaurantId is provided
  useEffect(() => {
    if (restaurantId) {
      fetchHours();
    }
  }, [restaurantId]);

  const fetchHours = async () => {
    if (!restaurantId) return;

    setLoading(true);
    setError(null);
    
    try {
      const hours = await getHours(restaurantId);
      
      if (hours && hours.length > 0) {
        // Convert array of hour documents to object by day
        const hoursObject = hours.reduce((acc, hourDoc) => {
          acc[hourDoc.day] = {
            isOpen: hourDoc.isOpen !== undefined ? hourDoc.isOpen : true,
            openTime: hourDoc.openTime || '09:00',
            closeTime: hourDoc.closeTime || '21:00'
          };
          return acc;
        }, {});
        
        // Ensure all days are represented
        const completeHours = DAYS_OF_WEEK.reduce((acc, day) => {
          acc[day] = hoursObject[day] || {
            isOpen: true,
            openTime: '09:00',
            closeTime: '21:00'
          };
          return acc;
        }, {});
        
        setHoursData(completeHours);
      }
    } catch (err) {
      console.error('Error fetching hours:', err);
      setError('Failed to load business hours');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!restaurantId) {
      setError('Please save the restaurant first before updating hours');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      await updateHours(restaurantId, hoursData);
      setSuccess(true);
      
      if (onChange) {
        onChange(hoursData);
      }
    } catch (err) {
      console.error('Error saving hours:', err);
      setError('Failed to save business hours');
    } finally {
      setSaving(false);
    }
  };

  const handleDayToggle = (day) => {
    setHoursData(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        isOpen: !prev[day].isOpen
      }
    }));
  };

  const handleTimeChange = (day, field, value) => {
    setHoursData(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const copyHoursToAll = (fromDay) => {
    const sourceHours = hoursData[fromDay];
    
    setHoursData(prev => {
      const newHours = { ...prev };
      
      DAYS_OF_WEEK.forEach(day => {
        if (day !== fromDay) {
          newHours[day] = { ...sourceHours };
        }
      });
      
      return newHours;
    });
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        {DAYS_OF_WEEK.map(day => (
          <Grid item xs={12} key={day}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">{day}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={hoursData[day]?.isOpen}
                          onChange={() => handleDayToggle(day)}
                          color="primary"
                        />
                      }
                      label={hoursData[day]?.isOpen ? "Open" : "Closed"}
                    />
                    <Button 
                      size="small" 
                      onClick={() => copyHoursToAll(day)}
                      sx={{ ml: 1 }}
                    >
                      Apply to All Days
                    </Button>
                  </Box>
                </Box>
                
                {hoursData[day]?.isOpen && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Opening Time"
                        type="time"
                        value={hoursData[day]?.openTime || ''}
                        onChange={(e) => handleTimeChange(day, 'openTime', e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          step: 300, // 5 min
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Closing Time"
                        type="time"
                        value={hoursData[day]?.closeTime || ''}
                        onChange={(e) => handleTimeChange(day, 'closeTime', e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          step: 300, // 5 min
                        }}
                      />
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !restaurantId}
        >
          {saving ? 'Saving...' : 'Save Hours'}
        </Button>
      </Box>

      {/* Error/Success messages */}
      <Snackbar 
        open={!!error || success} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={error ? 'error' : 'success'} 
          variant="filled"
        >
          {error || 'Business hours saved successfully'}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default HoursEditor;